import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { uploadReportAction } from "../../../store/actions/ReportAction";
import { showErrorAlert } from "../../../services/ErrorHandlingService";

const UploadReportModal = ({ show, onClose, patientId }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    reportTitle: "",
    reportDate: new Date().toISOString().split("T")[0],
    reportDescription: "",
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setPreviewIndex(0);
  };

  const removeFile = (index) => {
    const updated = [...selectedFiles];
    updated.splice(index, 1);
    setSelectedFiles(updated);
    if (previewIndex >= updated.length && updated.length > 0) {
      setPreviewIndex(updated.length - 1);
    }
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      showErrorAlert("Please select at least one file!");
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append("patient_id", patientId);
      data.append("report_title", formData.reportTitle);
      data.append("report_date", formData.reportDate);
      data.append("report_description", formData.reportDescription);
      selectedFiles.forEach((file) => {
        data.append("report_file[]", file);
      });

      const response = await dispatch(uploadReportAction(data));
      if (response?.status === 200) {
        handleClose();
      }
    } catch (error) {
      showErrorAlert("Failed to upload report!");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      reportTitle: "",
      reportDate: new Date().toISOString().split("T")[0],
      reportDescription: "",
    });
    setSelectedFiles([]);
    setPreviewIndex(0);
    onClose();
  };

  const getFilePreviewUrl = (file) => URL.createObjectURL(file);
  const isPDF = (file) => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

  const handlePrevious = () => {
    if (previewIndex > 0) setPreviewIndex(previewIndex - 1);
  };

  const handleNext = () => {
    if (previewIndex < selectedFiles.length - 1) setPreviewIndex(previewIndex + 1);
  };

  const currentFile = selectedFiles[previewIndex];
  const currentPreviewUrl = currentFile ? getFilePreviewUrl(currentFile) : null;
  const isCurrentPDF = currentFile ? isPDF(currentFile) : false;

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Upload Report</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Report Title *</Form.Label>
            <Form.Control
              type="text"
              name="reportTitle"
              value={formData.reportTitle}
              onChange={handleChange}
              placeholder="Enter report title"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Report Date *</Form.Label>
            <Form.Control
              type="date"
              name="reportDate"
              value={formData.reportDate}
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Upload File(s) *</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.pdf"
              onChange={handleFileChange}
            />
            <Form.Text className="text-muted">
              Supported formats: JPG, PNG, PDF. You can select multiple files.
            </Form.Text>
          </Form.Group>

          {selectedFiles.length > 0 && (
            <div className="mb-3 border rounded p-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Preview</h6>
                <span className="badge bg-primary">{selectedFiles.length} file(s) selected</span>
              </div>

              <div
                className="position-relative d-flex align-items-center justify-content-center"
                style={{
                  minHeight: "350px",
                  overflow: "visible",
                  padding: "0 25px"
                }}
              >
                {/* Left Arrow - always visible when multiple files */}
                {selectedFiles.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-outline-primary rounded-circle"
                    onClick={handlePrevious}
                    disabled={previewIndex === 0}
                    style={{
                      position: "absolute",
                      left: "-20px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: "45px",
                      height: "45px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 20,
                      backgroundColor: "white",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      border: "1px solid #dee2e6",
                      opacity: previewIndex === 0 ? 0.5 : 1,
                      cursor: previewIndex === 0 ? "not-allowed" : "pointer",
                      transition: "opacity 0.2s ease"
                    }}
                  >
                    <i className="fa fa-chevron-left fa-lg"></i>
                  </button>
                )}

                <div style={{ width: "100%", padding: selectedFiles.length > 1 ? "0 35px" : "0" }}>
                  {isCurrentPDF ? (
                    <div className="text-center py-5">
                      <i className="las la-file-pdf" style={{ fontSize: "100px", color: "#dc3545" }}></i>
                      <div className="mt-4">
                        <Button
                          variant="danger"
                          onClick={() => window.open(currentPreviewUrl, "_blank")}
                          size="lg"
                        >
                          <i className="fa fa-file-pdf-o me-2"></i>
                          Open PDF Document
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={currentPreviewUrl}
                      alt={`Preview ${previewIndex + 1}`}
                      style={{
                        width: "100%",
                        maxHeight: "55vh",
                        objectFit: "contain",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                      }}
                      onError={(e) => (e.target.src = "/placeholder-image.png")}
                    />
                  )}
                </div>

                {/* Right Arrow - always visible when multiple files */}
                {selectedFiles.length > 1 && (
                  <button
                    type="button"
                    className="btn btn-outline-primary rounded-circle"
                    onClick={handleNext}
                    disabled={previewIndex === selectedFiles.length - 1}
                    style={{
                      position: "absolute",
                      right: "-20px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: "45px",
                      height: "45px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 20,
                      backgroundColor: "white",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      border: "1px solid #dee2e6",
                      opacity: previewIndex === selectedFiles.length - 1 ? 0.5 : 1,
                      cursor: previewIndex === selectedFiles.length - 1 ? "not-allowed" : "pointer",
                      transition: "opacity 0.2s ease"
                    }}
                  >
                    <i className="fa fa-chevron-right fa-lg"></i>
                  </button>
                )}
              </div>

              {/* DOTS Navigation */}
              {selectedFiles.length > 1 && (
                <div className="d-flex justify-content-center mt-4 gap-3">
                  {selectedFiles.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setPreviewIndex(index)}
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        cursor: "pointer",
                        backgroundColor: previewIndex === index ? "#007bff" : "#dee2e6",
                        transition: "all 0.3s ease",
                        border: "none",
                        padding: "0",
                      }}
                      className="dot-indicator"
                      onMouseEnter={(e) => {
                        if (previewIndex !== index) {
                          e.currentTarget.style.backgroundColor = "#007bff";
                          e.currentTarget.style.opacity = "0.5";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (previewIndex !== index) {
                          e.currentTarget.style.backgroundColor = "#dee2e6";
                          e.currentTarget.style.opacity = "1";
                        }
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Counter Badge */}
              {selectedFiles.length > 1 && (
                <div className="text-center mt-3">
                  <span className="badge bg-secondary">
                    <i className="fa fa-file-image-o me-1"></i>
                    {previewIndex + 1} / {selectedFiles.length}
                  </span>
                </div>
              )}

              {/* Selected Files List */}
              <div className="mt-3">
                <h6 className="mb-2">Selected Files:</h6>
                <div style={{ maxHeight: "150px", overflowY: "auto" }}>
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className={`d-flex justify-content-between align-items-center border rounded p-2 mb-2 ${
                        previewIndex === index ? "border-primary bg-primary bg-opacity-10" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={() => setPreviewIndex(index)}
                    >
                      <div className="d-flex align-items-center gap-2">
                        {isPDF(file) ? (
                          <i className="las la-file-pdf" style={{ color: "#dc3545", fontSize: "20px" }}></i>
                        ) : (
                          <i className="fa fa-file-image-o" style={{ color: "#007bff", fontSize: "20px" }}></i>
                        )}
                        <span style={{ fontSize: "14px" }}>{file.name}</span>
                        <small className="text-muted">({(file.size / 1024).toFixed(1)} KB)</small>
                      </div>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                      >
                        ✕
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="reportDescription"
              value={formData.reportDescription}
              onChange={handleChange}
              placeholder="Optional description"
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Close</Button>
        <Button variant="primary" disabled={loading} onClick={handleSubmit}>
          {loading ? "Uploading..." : "Upload"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadReportModal;